import { Component } from '@angular/core';
import { RouterLink } from '@angular/router';

interface ProjectItem {
  title: string;
  category: string;
  franchaise?: string;
  product?: string;
  modules?: string;
  client?: string;
  url?: string;
  summary: string;
  highlights: string[];
}

@Component({
  standalone: true,
  selector: 'app-portfolio',
  imports: [RouterLink],
  templateUrl: './portfolio.component.html',
})
export class PortfolioComponent {
  readonly projects: ProjectItem[] = [
    {
      title: 'Norbix Enterprise',
      category: 'Unified Multi-Tenant Cloud ERP Suite',
      franchaise: 'Norbix Enterprise',
      product: 'Norbix Enterprise',
      modules: 'NovaHRM, NovaFLEET, NovaFinance, NovaCONNECTS',
      url: 'https://norbix.novarix.tech',
      summary:
        'A comprehensive multi-tenant cloud enterprise platform integrating human resources, fleet operations, financial ledgers, and secure prepaid communications under a single high-availability command layer with companion Android mobile apps.',
      highlights: [
        'Multi-tenant cloud architecture with row-level data isolation',
        'Unified modules: NovaHRM, NovaFLEET, NovaFinance, NovaCONNECTS',
        'Companion Android mobile apps (NovaHRM, NovaDRIVE, NovaCONNECTS)',
        'Cross-module financial ledger and GST-compliant invoicing',
        'Real-time GPS vehicle tracking and geofenced mobile attendance punch',
        'Automated background compliance, daily alerts & birthday wishes',
        'Multi-channel real-time notifications via SignalR and Firebase FCM',
        'Granular role-based access control and tenant white-label branding',
      ],
    },
    {
      title: 'NovaHRM',
      category: 'Workforce management, reimagined',
      franchaise: 'Norbix Enterprise',
      product: 'NovaHRM',
      modules: 'HRM Module',
      url: 'https://norbix.novarix.tech/products/novahrm',
      summary:
        'A dedicated human resource management platform for organizations that need employee lifecycle control, attendance intelligence, geofenced mobile punch, leave and payroll workflows, and compliance- ready documentation — all in one connected system.',
      highlights: [
        'Employee onboarding & profiles',
        'Attendance & correction tracking',
        'GPS mobile punch & photo proof',
       ' Live location & route tracking',
       ' Leave & balance management',
       ' Payroll & payslip generation',
       ' Document & compliance tracking',
       ' HRM & payroll reporting',
       ' Role - based access control',
      ],
    },
    {
      title: 'NovaFLEET',
      category: 'Complete transport enterprise suite',
      franchaise: 'Norbix Enterprise',
      product: 'NovaFleet',
      modules: 'HRM, Fleet, Finance Modules',
      url: 'https://norbix.novarix.tech/products/novafleet',
      summary:
        'An integrated operations platform combining workforce management, fleet operations, and financial accounting into one unified command layer. Built for transport-led organizations managing drivers, vehicles, trips, HR, and finances together.',
      highlights: [
        'NovaHRM: HR & payroll management',
        'NovaFleet: Fleet & asset tracking',
        'NovaFinance: Ledger & invoicing',
        'Real-time GPS & geo-fencing',
        'Instant maintenance & payroll alerts',
        'Full operations-to-finance audit',
        'Multi-role reporting dashboard',
        'Mobile app for drivers & updates',
      ],
    },
    {
      title: 'NovaConnects',
      category: 'Voice/video call & prepaid billing engine',
      franchaise: 'Norbix Enterprise',
      product: 'NovaConnects',
      modules: 'NovaConnects Module',
      url: 'https://norbix.novarix.tech/products/novaconnects',
      summary:
        'A communications and prepaid billing engine facilitating secure voice and video calls between parents and students. Enforces strict calling schedules, manages prepaid balances, and automates ledger transactions.',
      highlights: [
        'Parent & student registration',
        'Wallet & payment tracking',
        'Scheduled weekly calling',
        'Voice & video call rates',
        'Live audio/video calling',
        'Automatic wallet deductions',
        'Admin & college dashboard',
        'Call & payment reports',
        'Session & recharge logs',
      ],
    },
    {
      title: 'Norbix Compass',
      category: 'The complete Academic ERP Suite for Colleges & Universities',
      franchaise: 'Norbix Compass',
      product: 'Norbix Compass',
      modules: 'Norbix Compass Module',
      url: 'https://compass.novarix.tech',
      summary:
        'A comprehensive, secure multi-tenant platform purpose-built to unify all operations under one single database: student admissions & lifecycle, staff onboarding & allotments, biometric attendance, centralized payroll, digital library systems, transport routing, hostel allocation, and central fee fixation.',
      highlights: [
        'Multi-college administration',
        'Employee onboarding & records',
        'Salary, PF, ESI & tax setups',
        'Staff posting & transfers',
        'Student admission & lifecycle',
        'Fee & scholarship tracking',
        'Library books & circulation',
        'Bus routes & fleet details',
        'Hostels & room allocation',
      ],
    },
    {
      title: 'Smart Biometric Attendance & Access System',
      category: 'IoT + Enterprise Platform',
      client: 'Sri Balaji Educational Society (SBES), Anantapur',
      summary:
        'A scalable attendance and access control system integrating biometric devices with centralized cloud infrastructure.',
      highlights: [
        'Biometric device integration (real-time sync)',
        'Multi-location attendance tracking',
        'Role-based dashboards for administration',
        'Centralized reporting across institutions',
        'Secure data synchronization between devices and cloud',
      ],
    },
    {
      title: 'Secure Distributed Cloud Data Platform',
      category: 'Cloud + Security',
      summary:
        'A high-performance distributed cloud system designed for secure data transfer, storage, and task scheduling in untrusted environments.',
      highlights: [
        'Encrypted data transfer using advanced cryptographic models',
        'Secure deduplication techniques',
        'Optimized distributed task scheduling',
        'High-availability architecture',
        'Designed for performance and data integrity',
      ],
    },
    {
      title: 'IoT-Enabled Gate Security & Visitor Management',
      category: 'IoT + Security',
      client: 'Institutional Deployments',
      summary:
        'A smart access control system with real-time monitoring and secure entry management.',
      highlights: [
        'Barcode / ID-based access validation',
        'Visitor logging with photo capture',
        'Permission-based entry system',
        'Real-time tracking dashboards',
        'Secure audit logs and reporting',
      ],
    },
    {
      title: 'Online Examination & Assessment Platform',
      category: 'Enterprise Application',
      client: 'JNTUA / PVKKIT, Anantapur',
      summary:
        'A scalable online exam platform supporting large numbers of concurrent users with automated evaluation and session resilience.',
      highlights: [
        'MCQ-based dynamic exam system',
        'Session recovery & fault tolerance',
        'Automated evaluation & instant results',
        'Scheduling and multi-exam management',
        'Performance analytics',
      ],
    },
    {
      title: 'Institutional ERP & Student Management Platform',
      category: 'Enterprise + Cloud',
      client: 'SBES Campus, Anantapur',
      summary:
        'A comprehensive platform managing end-to-end institutional operations across admissions, fees, and employee lifecycle.',
      highlights: [
        'Admissions, attendance, fees, results',
        'Parent & student dashboards',
        'SMS / Email notification system',
        'Role-based access control',
        'Scalable architecture for large user bases',
      ],
    },
    {
      title: 'Library & Digital Resource Management System',
      category: 'Data Platform',
      summary:
        'A modern library system designed for efficient resource tracking and user management.',
      highlights: [
        'Inventory and catalog management',
        'Book search and reservation',
        'Automated fine calculation',
        'Usage analytics and reporting',
        'Secure and scalable backend',
      ],
    },
    {
      title: 'Grievance Redressal & Workflow Automation System',
      category: 'Automation',
      summary:
        'A transparent system for managing and resolving user complaints efficiently.',
      highlights: [
        'Complaint tracking with status updates',
        'Admin dashboards for resolution workflows',
        'Categorization and prioritization',
        'Analytics for recurring issues',
        'Secure and role-based access',
      ],
    },
    {
      title: 'Cloud-Based Admission & Enrollment Platform',
      category: 'Cloud Application',
      summary:
        'A fully automated admission system handling end-to-end student onboarding.',
      highlights: [
        'Application submission & document upload',
        'Fee management and confirmation workflows',
        'Automated communication',
        'Scalable cloud backend',
        'Secure data handling',
      ],
    },
  ];
}
